import { Canvas, Element, ImageCenteredElement } from '@thive/canvas';
import {
  Areas,
  Design,
  Flow,
  FlowLayer,
  FlowStop,
  Group,
  InterAreaConnection,
  JustInTimeRoutingConfig,
  LogicalSpace,
  Obstacle,
  Station,
  Templates,
  Track,
  VehicleLayout,
  LoadType,
} from '@thive/fddm-types';

import { UnifiedVehicleDetails } from '@/modules/vehicles/types/internal';
import {
  areasToFlatCollection,
  buildTagMatchRegExp,
  createEmptyAreas,
  createEmptyTemplates,
  pushAreasToItsCollection,
  templatesToFlatCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { FDDM_TAG, Tag } from '@/modules/floorplanDesign/constants';
import { merge } from '@/modules/common/helpers/array';
import { HIGHWAY_ARROW_ID } from '@/modules/canvas/constants/elements';
import { GeneratedLayerNames, LayerNames } from '@/modules/common/types/layers';

import {
  areaMapper,
  interAreaConnectionMapper,
  groupMapper,
  templateMapper,
  flowStopMapper,
  obstacleMapper,
  logicalSpaceMapper,
  flowMapper,
  poiMapper,
  stationMapper,
  trackMapper,
  vehicleLayoutPoiConnectionMapper,
  modelMapper,
} from '@/modules/canvas/mappers';
import { getIds, indexById } from './mapperHelpers';

export type FloorplanElementMapperLayer =
  | 'areas'
  | 'obstacles'
  | 'logicalSpaces'
  | 'jitRoutingConfigs'
  | 'interAreaConnections'
  | 'groups'
  | 'templateGroups'
  | 'flowStops'
  | 'flows'
  | 'stations'
  | 'pois'
  | 'gateMappings'
  | 'tracks'
  | 'referenceImage'
  | 'model';

export type FloorplanElementMapperOptions = Partial<Record<FloorplanElementMapperLayer, boolean>>;

export type FloorplanElementSource = {
  areas?: Areas;
  obstacles?: Obstacle[];
  interAreaConnections?: InterAreaConnection[];
  logicalSpaces?: LogicalSpace[];
  jitRoutingConfigs?: JustInTimeRoutingConfig[];
  templates?: Templates;
  groups?: Group[];
  flowLayer?: FlowLayer;
  stations?: Station[];
  vehicleLayouts?: VehicleLayout[];
  tracks?: Track[];
  referenceImage?: ImageCenteredElement;
};

type GetAreasByIds = (ids: string[]) => Areas;
type GetGroupsByAreaIds = (ids: string[]) => Group[];
type GetRelatedFlows = (ids: string[]) => Flow[];
type GetRelatedFlowStops = (ids: string[]) => FlowStop[];
type GetJitRoutingConfigsByIds = (ids: string[]) => JustInTimeRoutingConfig[];
type IsTagVisible = (tag: Tag) => boolean;
type IsLayerVisible = (layerName: LayerNames) => boolean;
type IsGeneratedLayerVisible = (vehicleTypeId: string, layerName: GeneratedLayerNames) => boolean;

type FloorplanElementMapper2DInput = {
  source: FloorplanElementSource;
  canvas: Canvas;
  zoom: number;
  selectedIds: string[];
  design: Design;
  vehicleLayouts: VehicleLayout[];
  vehicleDetails: UnifiedVehicleDetails[];
  getAreasByIds: GetAreasByIds;
  getGroupsByAreaIds: GetGroupsByAreaIds;
  getRelatedFlows: GetRelatedFlows;
  getRelatedFlowStops: GetRelatedFlowStops;
  getJitRoutingConfigsByIds: GetJitRoutingConfigsByIds;
  isTagVisible: IsTagVisible;
  isLayerVisible: IsLayerVisible;
  isGeneratedLayerVisible: IsGeneratedLayerVisible;
};

type FloorplanElementMapper3DInput = {
  areas: Areas;
  obstacles: Obstacle[];
  loadTypes: LoadType[];
  logicalSpaces: LogicalSpace[];
};

type MappedLogicalSpaces = {
  elements: Element[];
  trafficBoardIdsToRemove: string[];
};

export type FloorplanElementMapperResult = {
  elements: Element[];
  idsToRemove: string[];
};

const DEFAULT_OPTIONS: Required<FloorplanElementMapperOptions> = {
  areas: true,
  obstacles: true,
  logicalSpaces: true,
  jitRoutingConfigs: true,
  interAreaConnections: true,
  groups: true,
  templateGroups: true,
  flowStops: true,
  flows: true,
  stations: true,
  pois: true,
  gateMappings: true,
  tracks: true,
  referenceImage: true,
  model: true,
};

const LAYER_NAMES = Object.values(LayerNames);

const mergeById = <Item extends { id: string }>(source: Item[], related: Item[]): Item[] =>
  merge<Item>(source, related, (sourceItem, relatedItem) => sourceItem.id === relatedItem.id);

const createEmptyMappedLogicalSpaces = (): MappedLogicalSpaces => ({
  elements: [],
  trafficBoardIdsToRemove: [],
});

export class FloorplanElementMapper {
  private options: Required<FloorplanElementMapperOptions>;

  public constructor(options: FloorplanElementMapperOptions = {}) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
    };
  }

  public map2D({
    source,
    canvas,
    zoom,
    selectedIds,
    design,
    vehicleLayouts,
    vehicleDetails,
    getAreasByIds,
    getGroupsByAreaIds,
    getRelatedFlows,
    getRelatedFlowStops,
    getJitRoutingConfigsByIds,
    isTagVisible,
    isLayerVisible,
    isGeneratedLayerVisible,
  }: FloorplanElementMapper2DInput): FloorplanElementMapperResult {
    const sourceAreas = source.areas ?? createEmptyAreas();
    const sourceAreaIds = getIds(areasToFlatCollection(sourceAreas));
    const sourceAreaIdSet = new Set(sourceAreaIds);

    const sourceLogicalSpaces = source.logicalSpaces ?? [];
    const sourceLogicalSpaceIds = getIds(sourceLogicalSpaces);
    const sourceLogicalSpaceIdSet = new Set(sourceLogicalSpaceIds);

    const sourceObstacles = source.obstacles ?? [];
    const sourceObstacleIds = getIds(sourceObstacles);
    const sourceObstacleIdSet = new Set(sourceObstacleIds);

    const sourceGroupItemIds = [...sourceAreaIds, ...sourceLogicalSpaceIds, ...sourceObstacleIds];

    const sourceFlowStops = source.flowLayer?.flowStops ?? [];
    const sourceFlowStopIds = getIds(sourceFlowStops);

    const selectedAreas = getAreasByIds(selectedIds);
    const selectedAreaIds = getIds(areasToFlatCollection(selectedAreas));
    const highlightedFlowIds = getIds(getRelatedFlows(selectedAreaIds));

    const relatedFlowStops = getRelatedFlowStops(sourceAreaIds);
    const flowStops = mergeById(sourceFlowStops, relatedFlowStops);

    const sourceFlows = source.flowLayer?.flows ?? [];
    const relatedFlows = getRelatedFlows([...sourceAreaIds, ...sourceFlowStopIds]);
    const flows = mergeById(sourceFlows, relatedFlows);

    const sourceGroups = source.groups ?? [];
    const relatedGroups = getGroupsByAreaIds(sourceGroupItemIds);
    const uxGroups = mergeById(sourceGroups, relatedGroups);

    const groupedItemIds = new Set(uxGroups.flatMap((group) => group.areaIds));

    const relatedLogicalSpaces = design.logicalSpaces.filter((logicalSpace) => {
      if (!groupedItemIds.has(logicalSpace.id)) return false;

      return !sourceLogicalSpaceIdSet.has(logicalSpace.id);
    });
    const sourceAndRelatedLogicalSpaces = [...sourceLogicalSpaces, ...relatedLogicalSpaces];

    const relatedObstacles = design.obstacles.filter((obstacle) => {
      if (!groupedItemIds.has(obstacle.id)) return false;

      return !sourceObstacleIdSet.has(obstacle.id);
    });
    const sourceAndRelatedObstacles = [...sourceObstacles, ...relatedObstacles];

    const relatedAreas = createEmptyAreas();

    flowStops.forEach((flowStop) => {
      pushAreasToItsCollection(relatedAreas, getAreasByIds(flowStop.areaIds));
    });

    uxGroups.forEach((group) => {
      const relatedGroupAreaIds = group.areaIds.filter((id) => !sourceAreaIdSet.has(id));
      pushAreasToItsCollection(relatedAreas, getAreasByIds(relatedGroupAreaIds));
    });

    const sourceAndRelatedAreas = createEmptyAreas();
    pushAreasToItsCollection(sourceAndRelatedAreas, sourceAreas);
    pushAreasToItsCollection(sourceAndRelatedAreas, relatedAreas);

    const templateGroups = templatesToFlatCollection(
      source.templates ?? createEmptyTemplates(),
    ).map((template) => template.group);

    const areaElements = this.mapAreas(source, vehicleDetails, zoom, isLayerVisible);
    const obstacleElements = this.mapObstacles(source, isLayerVisible);
    const logicalSpaceResult = this.mapLogicalSpaces(source, isLayerVisible);
    const logicalSpaceElements = logicalSpaceResult.elements;
    const flowElements = this.mapFlows(flows, highlightedFlowIds, isLayerVisible);
    const interAreaConnectionElements = this.mapInterAreaConnections(
      source,
      isTagVisible,
      isLayerVisible,
    );
    const flowStopElements = this.mapFlowStops(flowStops, sourceAndRelatedAreas, isLayerVisible);
    const uxGroupElements = this.mapGroups(
      uxGroups,
      sourceAndRelatedAreas,
      sourceAndRelatedLogicalSpaces,
      sourceAndRelatedObstacles,
      isLayerVisible,
    );

    const templateGroupElements = this.mapTemplateGroups(templateGroups, isLayerVisible);

    const { childElements, childElementIds } = this.mapGroupChildren({
      canvas,
      areaElements,
      logicalSpaceElements,
      obstacleElements,
      templateGroups,
      uxGroups,
      relatedAreas,
      sourceAndRelatedLogicalSpaces,
      sourceAndRelatedObstacles,
    });

    const stationElements = this.mapStations(source, isLayerVisible);
    const poiElements = this.mapVehicleLayouts(
      source,
      isGeneratedLayerVisible,
      getJitRoutingConfigsByIds,
      zoom,
    );
    const trackElements = this.mapTracks(
      source,
      vehicleLayouts,
      isLayerVisible,
      isGeneratedLayerVisible,
    );
    const oldHighwayArrowIds = (source.areas?.highwayAreas ?? []).flatMap((area) =>
      canvas.getChildrenIds(area.id).filter((id) => id.endsWith(HIGHWAY_ARROW_ID)),
    );

    // When a connection stops using distCon rendering, the ${id}-line stripe is stale; queue it for removal when it's no longer produced.
    const newConnectionElementIds = new Set(getIds(interAreaConnectionElements));
    const staleConnectionLineIds = (source.interAreaConnections ?? [])
      .map((connection) => `${connection.id}-line`)
      .filter((lineId) => !newConnectionElementIds.has(lineId));

    const renderedTrackElementIds = new Set(getIds(trackElements));
    const staleTrackElementIds = source.tracks
      ? canvas
          .getElementsByTag(buildTagMatchRegExp([FDDM_TAG.TRACK_PIECE, FDDM_TAG.SWEPT_PATH]))
          .filter((element) => !renderedTrackElementIds.has(element.id))
          .map((element) => element.id)
      : [];

    const idsToRemove = [
      ...new Set([
        ...oldHighwayArrowIds,
        ...logicalSpaceResult.trafficBoardIdsToRemove,
        ...staleConnectionLineIds,
        ...staleTrackElementIds,
      ]),
    ];
    const elements = [
      ...templateGroupElements,
      ...uxGroupElements,
      ...childElements,
      ...areaElements.filter((element) => !childElementIds.has(element.id)),
      ...obstacleElements,
      ...logicalSpaceElements.filter((element) => !childElementIds.has(element.id)),
      ...flowElements,
      ...interAreaConnectionElements,
      ...stationElements,
      ...poiElements,
      ...trackElements,
      ...flowStopElements,
      ...this.mapReferenceImage(source, isLayerVisible),
    ];

    return {
      elements,
      idsToRemove,
    };
  }

  public map3D({
    areas,
    obstacles,
    loadTypes,
    logicalSpaces,
  }: FloorplanElementMapper3DInput): Element[] {
    if (!this.options.model) return [];

    return modelMapper(areas, obstacles, loadTypes, logicalSpaces);
  }

  private mapAreas(
    source: FloorplanElementSource,
    vehicleDetails: UnifiedVehicleDetails[],
    zoom: number,
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.areas) return [];
    if (!source.areas) return [];

    const elements = areaMapper(source.areas, vehicleDetails, zoom);

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.FLOORPLAN]);
  }

  private mapObstacles(source: FloorplanElementSource, isLayerVisible: IsLayerVisible): Element[] {
    if (!this.options.obstacles) return [];
    if (!source.obstacles) return [];

    const elements = obstacleMapper(source.obstacles);

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.INTERIOR]);
  }

  private mapLogicalSpaces(
    source: FloorplanElementSource,
    isLayerVisible: IsLayerVisible,
  ): MappedLogicalSpaces {
    if (!this.options.logicalSpaces) {
      return createEmptyMappedLogicalSpaces();
    }

    if (!source.logicalSpaces) {
      return createEmptyMappedLogicalSpaces();
    }

    const result = logicalSpaceMapper(source.logicalSpaces);

    return {
      ...result,
      elements: this.applyLayerVisibility(result.elements, isLayerVisible, [
        LayerNames.LOGICAL_SPACES,
      ]),
    };
  }

  private mapFlows(
    flows: Flow[],
    highlightedFlowIds: string[],
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.flows) return [];

    const elements = flowMapper(flows, highlightedFlowIds);

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.FLOWS]);
  }

  private mapInterAreaConnections(
    source: FloorplanElementSource,
    isTagVisible: IsTagVisible,
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.interAreaConnections) return [];
    if (!source.interAreaConnections) return [];

    const connectionsVisible = this.isTagAndLayersVisible(
      FDDM_TAG.BUBBLE,
      [LayerNames.CONNECTIONS],
      isTagVisible,
      isLayerVisible,
    );

    return interAreaConnectionMapper(source.interAreaConnections, connectionsVisible);
  }

  private mapFlowStops(
    flowStops: FlowStop[],
    sourceAndRelatedAreas: Areas,
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.flowStops) return [];

    const elements = flowStopMapper(flowStops, sourceAndRelatedAreas);

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.FLOWS]);
  }

  private mapGroups(
    uxGroups: Group[],
    sourceAndRelatedAreas: Areas,
    sourceAndRelatedLogicalSpaces: LogicalSpace[],
    sourceAndRelatedObstacles: Obstacle[],
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.groups) return [];

    const elements = groupMapper(
      uxGroups,
      sourceAndRelatedAreas,
      sourceAndRelatedLogicalSpaces,
      sourceAndRelatedObstacles,
    );

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.GROUPS]);
  }

  private mapTemplateGroups(templateGroups: Group[], isLayerVisible: IsLayerVisible): Element[] {
    if (!this.options.templateGroups) return [];

    const elements = templateMapper(templateGroups);

    return this.applyLayerVisibility(elements, isLayerVisible, [LayerNames.FLOORPLAN]);
  }

  private mapGroupChildren({
    canvas,
    areaElements,
    logicalSpaceElements,
    obstacleElements,
    templateGroups,
    uxGroups,
    relatedAreas,
    sourceAndRelatedLogicalSpaces,
    sourceAndRelatedObstacles,
  }: {
    canvas: Canvas;
    areaElements: Element[];
    logicalSpaceElements: Element[];
    obstacleElements: Element[];
    templateGroups: Group[];
    uxGroups: Group[];
    relatedAreas: Areas;
    sourceAndRelatedLogicalSpaces: LogicalSpace[];
    sourceAndRelatedObstacles: Obstacle[];
  }): {
    childElements: Element[];
    childElementIds: Set<string>;
  } {
    const childElements: Element[] = [];
    const childElementIds = new Set<string>();

    if (!this.options.groups && !this.options.templateGroups) {
      return {
        childElements,
        childElementIds,
      };
    }

    const relatedAreaIds = getIds(areasToFlatCollection(relatedAreas));
    const areaElementIds = getIds(areaElements);
    const areaElementsById = indexById(areaElements);
    const relatedAreaElementsById = indexById(
      canvas.getElements(relatedAreaIds.filter((id) => !areaElementsById.has(id))),
    );

    const logicalSpaceElementIds = getIds(logicalSpaceElements);
    const logicalSpaceElementsById = indexById(logicalSpaceElements);
    const sourceAndRelatedLogicalSpaceIds = getIds(sourceAndRelatedLogicalSpaces);
    const relatedLogicalSpaceElementsById = indexById(
      canvas.getElements(
        sourceAndRelatedLogicalSpaceIds.filter((id) => !logicalSpaceElementsById.has(id)),
      ),
    );

    const obstacleElementIds = getIds(obstacleElements);
    const obstacleElementsById = indexById(obstacleElements);
    const sourceAndRelatedObstacleIds = getIds(sourceAndRelatedObstacles);
    const relatedObstacleElementsById = indexById(
      canvas.getElements(sourceAndRelatedObstacleIds.filter((id) => !obstacleElementsById.has(id))),
    );

    [...templateGroups, ...uxGroups].forEach((group) => {
      const groupItemIds = new Set(group.areaIds);
      const groupChildElements: Element[] = [];

      areaElementIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;

        const areaElement = areaElementsById.get(id);
        if (areaElement) groupChildElements.push(areaElement);
      });

      relatedAreaIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;
        if (areaElementsById.has(id)) return;

        const areaElement = relatedAreaElementsById.get(id);
        if (areaElement) groupChildElements.push(areaElement);
      });

      logicalSpaceElementIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;

        const logicalSpaceElement = logicalSpaceElementsById.get(id);
        if (logicalSpaceElement) groupChildElements.push(logicalSpaceElement);
      });

      sourceAndRelatedLogicalSpaceIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;
        if (logicalSpaceElementsById.has(id)) return;

        const logicalSpaceElement = relatedLogicalSpaceElementsById.get(id);
        if (logicalSpaceElement) groupChildElements.push(logicalSpaceElement);
      });

      obstacleElementIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;

        const obstacleElement = obstacleElementsById.get(id);
        if (obstacleElement) groupChildElements.push(obstacleElement);
      });

      sourceAndRelatedObstacleIds.forEach((id) => {
        if (!groupItemIds.has(id)) return;
        if (obstacleElementsById.has(id)) return;

        const obstacleElement = relatedObstacleElementsById.get(id);
        if (obstacleElement) groupChildElements.push(obstacleElement);
      });

      groupChildElements.forEach((childElement) => {
        childElementIds.add(childElement.id);

        childElements.push({
          ...childElement,
          parentId: group.id,
          interactivity: {
            ...childElement.interactivity,
            selectable: true,
            hoverable: true,
          },
        });
      });
    });

    return {
      childElements,
      childElementIds,
    };
  }

  private mapStations(source: FloorplanElementSource, isLayerVisible: IsLayerVisible): Element[] {
    if (!this.options.stations) return [];
    if (!source.stations) return [];

    return stationMapper(source.stations, isLayerVisible(LayerNames.GENERATED_STATIONS));
  }

  private mapVehicleLayouts(
    source: FloorplanElementSource,
    isGeneratedLayerVisible: IsGeneratedLayerVisible,
    getJitRoutingConfigsByIds: GetJitRoutingConfigsByIds,
    zoom: number,
  ): Element[] {
    if (!this.options.pois) return [];
    if (!source.vehicleLayouts) return [];

    return source.vehicleLayouts.flatMap((vehicleLayout) => {
      const vehicleLayoutPoiConnectionsVisible = isGeneratedLayerVisible(
        vehicleLayout.vehicleTypeId,
        GeneratedLayerNames.POI_CONNECTIONS,
      );
      const poisVisible = isGeneratedLayerVisible(
        vehicleLayout.vehicleTypeId,
        GeneratedLayerNames.POIS,
      );
      const jitRoutingConfigIds = vehicleLayout.pois.flatMap(
        (poi) => poi.jitRoutingConfigIds ?? [],
      );
      const jitRoutingConfigs = this.options.jitRoutingConfigs
        ? getJitRoutingConfigsByIds(jitRoutingConfigIds)
        : [];

      return [
        ...vehicleLayoutPoiConnectionMapper(
          vehicleLayout.poiConnections,
          vehicleLayout.pois,
          vehicleLayoutPoiConnectionsVisible,
        ),
        ...poiMapper(vehicleLayout.pois, jitRoutingConfigs, zoom, poisVisible, poisVisible),
      ];
    });
  }

  private mapTracks(
    source: FloorplanElementSource,
    vehicleLayouts: VehicleLayout[],
    isLayerVisible: IsLayerVisible,
    isGeneratedLayerVisible: IsGeneratedLayerVisible,
  ): Element[] {
    if (!this.options.tracks) return [];
    if (!source.tracks || source.tracks.length === 0) return [];

    const vehicleTypeIdByPoiConnectionId = new Map<string, string>();

    vehicleLayouts.forEach((vehicleLayout) => {
      vehicleLayout.poiConnections.forEach((poiConnection) => {
        vehicleTypeIdByPoiConnectionId.set(poiConnection.id, vehicleLayout.vehicleTypeId);
      });
    });

    const tracksByVehicleTypeId = new Map<string, Track[]>();
    const tracksWithoutVehicleType: Track[] = [];

    source.tracks.forEach((track) => {
      const vehicleTypeId = vehicleTypeIdByPoiConnectionId.get(track.poiConnectionId);

      if (!vehicleTypeId) {
        tracksWithoutVehicleType.push(track);
        return;
      }

      const vehicleTracks = tracksByVehicleTypeId.get(vehicleTypeId) ?? [];
      vehicleTracks.push(track);
      tracksByVehicleTypeId.set(vehicleTypeId, vehicleTracks);
    });

    const vehicleTrackElements = [...tracksByVehicleTypeId.entries()].flatMap(
      ([vehicleTypeId, tracks]) =>
        trackMapper(
          tracks,
          isGeneratedLayerVisible(vehicleTypeId, GeneratedLayerNames.TRACKS),
          isGeneratedLayerVisible(vehicleTypeId, GeneratedLayerNames.SWEPT_PATHS),
          vehicleTypeId,
        ),
    );

    const fallbackTrackElements =
      tracksWithoutVehicleType.length > 0
        ? trackMapper(
            tracksWithoutVehicleType,
            isLayerVisible(LayerNames.TRACKS),
            isLayerVisible(LayerNames.SWEPT_PATHS),
          )
        : [];

    return [...vehicleTrackElements, ...fallbackTrackElements];
  }

  private mapReferenceImage(
    source: FloorplanElementSource,
    isLayerVisible: IsLayerVisible,
  ): Element[] {
    if (!this.options.referenceImage) return [];
    if (!source.referenceImage) return [];

    return this.applyLayerVisibility([source.referenceImage], isLayerVisible, [
      LayerNames.REFERENCE,
    ]);
  }

  private applyLayerVisibility(
    elements: Element[],
    isLayerVisible: IsLayerVisible,
    fallbackLayerNames: LayerNames[] = [],
  ): Element[] {
    return elements.map((element) => {
      const layerNames = this.getElementLayerNames(element, fallbackLayerNames);
      if (layerNames.length === 0) return element;

      return {
        ...element,
        visible:
          this.isElementVisible(element) && this.areLayersVisible(layerNames, isLayerVisible),
      };
    });
  }

  private getElementLayerNames(element: Element, fallbackLayerNames: LayerNames[]): LayerNames[] {
    const tagLayerNames = LAYER_NAMES.filter((layerName) => element.tags?.includes(layerName));

    return [...new Set([...fallbackLayerNames, ...tagLayerNames])];
  }

  private areLayersVisible(layerNames: LayerNames[], isLayerVisible: IsLayerVisible): boolean {
    return layerNames.every((layerName) => isLayerVisible(layerName));
  }

  private isTagAndLayersVisible(
    tag: Tag,
    layerNames: LayerNames[],
    isTagVisible: IsTagVisible,
    isLayerVisible: IsLayerVisible,
  ): boolean {
    return isTagVisible(tag) && this.areLayersVisible(layerNames, isLayerVisible);
  }

  private isElementVisible(element: Element): boolean {
    return element.visible !== false;
  }
}
